import { useState, useEffect, useCallback } from 'react';
import type { RefObject } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';

import type { CellPosition } from '../TableCellMenu';

interface UseTableCellMenuParams {
  tiptapEditor: TiptapEditor | null;
  iframeRef: RefObject<HTMLIFrameElement>;
}

interface UseTableCellMenuReturn {
  cellPosition: CellPosition | null;
  isOverMenu: boolean;
  setIsOverMenu: (value: boolean) => void;
  addRowBelow: () => void;
  addRowAbove: () => void;
  addColumnAfter: () => void;
  addColumnBefore: () => void;
  deleteRow: () => void;
  deleteColumn: () => void;
  deleteTable: () => void;
}

export default function useTableCellMenu({
  tiptapEditor,
  iframeRef
}: UseTableCellMenuParams): UseTableCellMenuReturn {
  const [cellPosition, setCellPosition] = useState<CellPosition | null>(null);
  const [isOverMenu, setIsOverMenu] = useState(false);

  const updateCellPosition = useCallback(() => {
    if (!tiptapEditor || !iframeRef.current) {
      setCellPosition(null);
      return;
    }

    const isInTable =
      tiptapEditor.isActive('table') ||
      tiptapEditor.isActive('tableCell') ||
      tiptapEditor.isActive('tableHeader');

    if (!isInTable) {
      if (!isOverMenu) {
        setCellPosition(null);
      }
      return;
    }

    const iframeDoc = iframeRef.current.contentDocument;
    if (!iframeDoc) {
      setCellPosition(null);
      return;
    }

    const selection = iframeDoc.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setCellPosition(null);
      return;
    }

    const anchorNode = selection.anchorNode;
    if (!anchorNode) {
      setCellPosition(null);
      return;
    }

    // anchorNode can be a Text node; get the parent element if so
    const element =
      anchorNode.nodeType === Node.ELEMENT_NODE
        ? (anchorNode as HTMLElement)
        : anchorNode.parentElement;

    const cellEl = element?.closest('td') ?? element?.closest('th') ?? null;

    if (!cellEl) {
      setCellPosition(null);
      return;
    }

    const cellRect = cellEl.getBoundingClientRect();
    const iframeRect = iframeRef.current.getBoundingClientRect();

    setCellPosition({
      top: iframeRect.top + cellRect.top,
      left: iframeRect.left + cellRect.left,
      width: cellRect.width,
      height: cellRect.height
    });
  }, [tiptapEditor, iframeRef, isOverMenu]);

  useEffect(() => {
    if (!tiptapEditor) return;

    updateCellPosition();
    tiptapEditor.on('selectionUpdate', updateCellPosition);
    tiptapEditor.on('transaction', updateCellPosition);

    // Update position on resize and scroll so menu follows the cell
    window.addEventListener('resize', updateCellPosition);
    window.addEventListener('scroll', updateCellPosition, true);

    // Also listen for scroll within the iframe
    const iframeWindow = iframeRef.current?.contentWindow;
    iframeWindow?.addEventListener('scroll', updateCellPosition, true);

    return () => {
      tiptapEditor.off('selectionUpdate', updateCellPosition);
      tiptapEditor.off('transaction', updateCellPosition);
      window.removeEventListener('resize', updateCellPosition);
      window.removeEventListener('scroll', updateCellPosition, true);
      iframeWindow?.removeEventListener('scroll', updateCellPosition, true);
    };
  }, [tiptapEditor, iframeRef, updateCellPosition]);

  const addRowBelow = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().addRowAfter().run();
  }, [tiptapEditor]);

  const addRowAbove = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().addRowBefore().run();
  }, [tiptapEditor]);

  const addColumnAfter = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().addColumnAfter().run();
  }, [tiptapEditor]);

  const addColumnBefore = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().addColumnBefore().run();
  }, [tiptapEditor]);

  const deleteRow = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().deleteRow().run();
  }, [tiptapEditor]);

  const deleteColumn = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().deleteColumn().run();
  }, [tiptapEditor]);

  const deleteTable = useCallback(() => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus().deleteTable().run();
  }, [tiptapEditor]);

  return {
    cellPosition,
    isOverMenu,
    setIsOverMenu,
    addRowBelow,
    addRowAbove,
    addColumnAfter,
    addColumnBefore,
    deleteRow,
    deleteColumn,
    deleteTable
  };
}
